function frozenProgressStages(stages) {
  return Object.freeze(
    stages.map(([afterMs, message]) => Object.freeze({ afterMs, message })),
  );
}

const SLOW_TOOL_PROGRESS_SEQUENCES = Object.freeze({
  'Revisando el documento…': frozenProgressStages([
    [20_000, 'La revisión continúa; sigo procesando el contenido…'],
    [30_000, 'Sigo leyendo el documento; todavía estoy en ello…'],
    [40_000, 'La revisión avanza. Continúo recorriendo el contenido…'],
    [50_000, 'Sigo revisando el documento. Los documentos extensos pueden tardar un poco más…'],
    [60_000, 'Sigo analizando el texto; el proceso no se ha detenido…'],
    [70_000, 'La revisión sigue. Estoy procesando más del documento…'],
    [80_000, 'Sigo revisando con calma para no pasar nada por alto…'],
    [90_000, 'La revisión sigue en curso. No necesitas volver a enviar la solicitud…'],
    [120_000, 'Todavía estoy leyendo el documento y extrayendo lo relevante…'],
    [150_000, 'La revisión avanza; sigo analizando el contenido con calma…'],
    [180_000, 'Sigo trabajando en el documento. El proceso no se ha detenido…'],
    [210_000, 'Continúo revisando. Este paso puede tomar unos minutos más…'],
    [240_000, 'Sigo procesando el texto para no omitir información importante…'],
    [270_000, 'La revisión sigue activa. Aún no hace falta reenviar nada…'],
    [300_000, 'Estoy avanzando con el documento; gracias por esperar…'],
    [360_000, 'Sigo en la revisión. Los documentos largos requieren más tiempo…'],
    [420_000, 'El análisis continúa. Conservo el progreso de esta solicitud…'],
    [480_000, 'Sigo revisando sin interrupciones…'],
    [540_000, 'La revisión sigue en marcha. Te avisaré cuando termine…'],
  ]),
  'Preparando el documento solicitado…': frozenProgressStages([
    [20_000, 'La preparación continúa; sigo procesando y organizando el contenido…'],
    [30_000, 'Sigo preparando el documento; todavía estoy organizando el material…'],
    [40_000, 'La preparación avanza. Continúo armando el contenido…'],
    [50_000, 'Sigo preparando el documento. Los textos extensos pueden tardar un poco más…'],
    [60_000, 'Sigo redactando; el proceso no se ha detenido…'],
    [70_000, 'La preparación sigue. Estoy ordenando más del texto…'],
    [80_000, 'Sigo preparando el contenido con calma para que quede claro…'],
    [90_000, 'La preparación sigue en curso. No necesitas volver a enviar la solicitud…'],
    [120_000, 'Todavía estoy organizando el contenido del documento…'],
    [150_000, 'La preparación avanza; sigo estructurando el texto…'],
    [180_000, 'Sigo trabajando en el documento. El proceso no se ha detenido…'],
    [210_000, 'Continúo preparando el contenido. Este paso puede tomar unos minutos más…'],
    [240_000, 'Sigo redactando y ordenando el texto para que quede claro…'],
    [270_000, 'La preparación sigue activa. Aún no hace falta reenviar nada…'],
    [300_000, 'Estoy avanzando con el documento; gracias por esperar…'],
    [360_000, 'Sigo preparando el texto. Los documentos largos requieren más tiempo…'],
    [420_000, 'La composición continúa. Conservo el progreso de esta solicitud…'],
    [480_000, 'Sigo preparando el documento sin interrupciones…'],
    [540_000, 'La preparación sigue en marcha. Te avisaré cuando termine…'],
  ]),
});

export function slowToolProgressSequence(anchorMessage) {
  return SLOW_TOOL_PROGRESS_SEQUENCES[String(anchorMessage || '').trim()] || null;
}

export function createSlowToolProgressController({
  setTimeoutFn = (callback, delay) => globalThis.setTimeout(callback, delay),
  clearTimeoutFn = timeoutId => globalThis.clearTimeout(timeoutId),
  now = () => Date.now(),
  onUpdate = () => {},
} = {}) {
  let timeoutId = null;
  let generation = 0;
  let activeState = null;

  const clearScheduledTimeout = () => {
    if (timeoutId !== null) {
      clearTimeoutFn(timeoutId);
      timeoutId = null;
    }
  };

  const stop = () => {
    generation += 1;
    clearScheduledTimeout();
    activeState = null;
    onUpdate('', { key: '' });
  };

  const tick = token => {
    if (token !== generation || !activeState) return;
    if (typeof activeState.isActive === 'function' && !activeState.isActive()) {
      stop();
      return;
    }

    const elapsedMs = Math.max(0, now() - activeState.startedAt);
    let visibleStageIndex = -1;
    activeState.sequence.forEach((stage, index) => {
      if (elapsedMs >= stage.afterMs) visibleStageIndex = index;
    });

    if (visibleStageIndex !== activeState.visibleStageIndex) {
      activeState.visibleStageIndex = visibleStageIndex;
      onUpdate(
        visibleStageIndex >= 0
          ? activeState.sequence[visibleStageIndex].message
          : '',
        { key: activeState.key },
      );
    }

    const nextStage = activeState.sequence[visibleStageIndex + 1];
    if (!nextStage) return;
    const remainingMs = Math.max(
      0,
      activeState.startedAt + nextStage.afterMs - now(),
    );
    timeoutId = setTimeoutFn(() => {
      timeoutId = null;
      tick(token);
    }, remainingMs);
  };

  const start = ({ anchorMessage, startedAt, key, isActive } = {}) => {
    const sequence = slowToolProgressSequence(anchorMessage);
    if (!sequence || !String(key || '').trim()) {
      stop();
      return false;
    }

    const normalizedStartedAt = Number(startedAt);
    const currentTime = now();
    const safeStartedAt = Number.isFinite(normalizedStartedAt)
      && normalizedStartedAt > 0
      && normalizedStartedAt <= currentTime
      ? normalizedStartedAt
      : currentTime;

    generation += 1;
    clearScheduledTimeout();
    activeState = {
      sequence,
      startedAt: safeStartedAt,
      key: String(key),
      isActive,
      visibleStageIndex: -1,
    };
    onUpdate('', { key: activeState.key });
    tick(generation);
    return true;
  };

  return Object.freeze({
    start,
    stop,
    activeKey: () => activeState && activeState.key || '',
  });
}
